import { useTranslations } from '@vocab/react';
import {
  Box,
  Stack,
  Text,
  Heading,
  useResponsiveValue,
} from 'braid-design-system';
import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import {
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { ViewAdLink } from 'src/app/v1/views/components/ViewAdLink';
import type { AppState } from 'src/types/appState';

import translations from './.vocab';
import Hand from './Hand';

export const ThankYouHand = () => {
  const { firstName, flowType } = useSelector(
    (state: AppState) => ({
      firstName: state.session.userFirstName,
      flowType: state.formData.flowType,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const getAction = () => {
    if (flowType === 'refresh') {
      return t('thank-you-tick-action-refresh');
    } else if (flowType === 'edit') {
      return t('thank-you-tick-action-edit');
    }
    return t('thank-you-tick-action-create');
  };

  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);

  const isShowTextOnSEEK = () => (flowType === 'create' ? ' on SEEK' : '');
  const getThankYouTickHeading = () => {
    if (isPerUnitBilling) {
      return t('thank-you-tick-heading-cpqa', {
        firstName,
      });
    }
    const translationName =
      flowType === 'create'
        ? 'thank-you-tick-heading'
        : 'thank-you-tick-heading-2';
    return t(translationName, {
      firstName,
    });
  };

  const subHeadingTranslation = isPerUnitBilling
    ? 'thank-you-tick-subheading-cpqa'
    : 'thank-you-tick-subheading';

  const responsiveValue = useResponsiveValue();

  return (
    <Box
      background="customDark"
      paddingY="xlarge"
      paddingX="gutter"
      borderRadius="xlarge"
      style={{
        backgroundImage:
          responsiveValue({
            desktop: `url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4MCIgaGVpZ2h0PSIyOTIiIHZpZXdCb3g9IjAgMCAxMjgwIDI5MiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzQ5NjBfMzMyOCkiPgo8Y2lyY2xlIGN4PSIxNS41IiBjeT0iLTQ3LjUiIHI9IjE5MS41IiBmaWxsPSIjMUU0N0E5Ii8+CjxjaXJjbGUgY3g9IjIyNi41IiBjeT0iMTEzLjUiIHI9IjI5LjUiIGZpbGw9IiNFNjAyNzciLz4KPC9nPgo8ZGVmcz4KPGNsaXBQYXRoIGlkPSJjbGlwMF80OTYwXzMzMjgiPgo8cmVjdCB3aWR0aD0iMTI4MCIgaGVpZ2h0PSIyOTIiIHJ4PSIyNCIgZmlsbD0id2hpdGUiLz4KPC9jbGlwUGF0aD4KPC9kZWZzPgo8L3N2Zz4K),
             url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTI4MCIgaGVpZ2h0PSIyOTIiIHZpZXdCb3g9IjAgMCAxMjgwIDI5MiIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgY2xpcC1wYXRoPSJ1cmwoI2NsaXAwXzQ5NjBfMzMzMykiPgo8Y2lyY2xlIGN4PSIxMjQ4IiBjeT0iMjgiIHI9IjEwNiIgZmlsbD0iI0U2MDI3NyIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTEwNTcgMjU2LjU4MUMxMDI4LjA1IDI1Ni41ODEgMTAwNC41OCAyODAuMDUgMTAwNC41OCAzMDlDMTAwNC41OCAzMzcuOTUgMTAyOC4wNSAzNjEuNDE5IDEwNTcgMzYxLjQxOUMxMDg1Ljk1IDM2MS40MTkgMTEwOS40MiAzMzcuOTUgMTEwOS40MiAzMDlDMTEwOS40MiAyODAuMDUgMTA4NS45NSAyNTYuNTgxIDEwNTcgMjU2LjU4MVpNOTMyIDMwOUM5MzIgMjM5Ljk2NCA5ODcuOTY0IDE4NCAxMDU3IDE4NEMxMTI2LjA0IDE4NCAxMTgyIDIzOS45NjQgMTE4MiAzMDlDMTE4MiAzNzguMDM2IDExMjYuMDQgNDM0IDEwNTcgNDM0Qzk4Ny45NjQgNDM0IDkzMiAzNzguMDM2IDkzMiAzMDlaIiBmaWxsPSIjMUU0N0E5Ii8+CjwvZz4KPGRlZnM+CjxjbGlwUGF0aCBpZD0iY2xpcDBfNDk2MF8zMzMzIj4KPHJlY3Qgd2lkdGg9IjEyODAiIGhlaWdodD0iMjkyIiByeD0iMjQiIGZpbGw9IndoaXRlIi8+CjwvY2xpcFBhdGg+CjwvZGVmcz4KPC9zdmc+Cg==)`,
            mobile: '',
            tablet: '',
          }) || undefined,
        backgroundPosition: `0 0, 100% 0`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'auto 300px',
        backgroundColor: '#122F83',
      }}
    >
      <Stack space="large" align="center">
        <Hand />

        <Heading level="2" align="center">
          {getThankYouTickHeading()}
        </Heading>

        <Text align="center">
          {`${
            t(subHeadingTranslation, {
              actionName: getAction(),
            }) + isShowTextOnSEEK()
          }.`}{' '}
          {!isPerUnitBilling && <ViewAdLink />}
        </Text>
      </Stack>
    </Box>
  );
};
